import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import { useAuth } from '../context/AuthContext';
import loadImage from '../assets/imageLoader';
import { 
    FaUser, FaCheckCircle, FaPlay, FaFlagCheckered, FaHourglassHalf, 
    FaTimesCircle, FaCalendarAlt, FaMoneyBillWave, FaClock, FaMapMarkerAlt, FaStar
} from 'react-icons/fa';
import JobTimer from '../components/JobTimer';
import RatingModal from '../components/RatingModal';

const StatusBadge = ({ status }) => {
    const colors = {
        pending: 'bg-yellow-100 text-yellow-800',
        accepted: 'bg-blue-100 text-blue-800',
        starting: 'bg-purple-100 text-purple-800', // pending_start
        pending_start: 'bg-purple-100 text-purple-800',
        active: 'bg-green-100 text-green-800', // running
        running: 'bg-green-100 text-green-800',
        reviewing: 'bg-orange-100 text-orange-800', // pending_end
        pending_end: 'bg-orange-100 text-orange-800',
        completed: 'bg-slate-800 text-white',
        cancelled: 'bg-red-100 text-red-800',
        rejected: 'bg-red-100 text-red-800'
    };
    
    // Normalize display text
    const displayStatus = status.replace('_', ' ');

    return (
        <span className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${colors[status] || 'bg-gray-100'}`}>
            {displayStatus}
        </span>
    );
};

const MyHiresPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [hires, setHires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Rating State
    const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
    const [ratingData, setRatingData] = useState({ workerId: '', workerName: '', hireId: '', type: '' });

    const fetchHires = async () => {
        try {
            setLoading(true);
            const res = await apiClient.get('/hires/my-hires');
            setHires(res.data);
        } catch (err) {
            setError('Failed to fetch hires.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHires();
    }, []);

    const updateStatus = async (id, newStatus) => {
        const actionText = 
            newStatus === 'running' ? 'start the job' : 
            newStatus === 'completed' ? 'confirm completion and pay' : 
            newStatus;

        if (!window.confirm(`Are you sure you want to ${actionText}?`)) return;
        
        try {
            await apiClient.put(`/hires/${id}/status`, { status: newStatus });
            fetchHires(); // Refresh list
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to update status');
        }
    };

    if (loading) return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800"></div></div>;
    if (error) return <div className="p-10 text-center text-red-500 font-bold">{error}</div>;

    return (
        <div className="font-sans min-h-screen pb-20 px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between mb-8 pt-6">
                <div>
                    <h1 className="text-3xl font-black text-slate-800 tracking-tight text-center md:text-left">
                        {user.role === 'buyer' ? 'Hired Workers' : 'My Contracts'}
                    </h1>
                    <p className="text-slate-500 font-medium mt-1 text-center md:text-left">Track and manage your active work contracts.</p>
                </div>
            </div>

            <div className="grid gap-6 max-w-6xl mx-auto">
                {hires.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-[2rem] border border-dashed border-slate-200 shadow-sm">
                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300 shadow-inner">
                            <FaUser className="text-3xl" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-700">No active contracts</h3>
                        <p className="text-slate-500 mt-1">You haven't hired anyone yet.</p>
                    </div>
                ) : (
                    hires.map(hire => {
                        const isBuyer = user.role === 'buyer';
                        const otherParty = isBuyer ? hire.worker : hire.buyer;
                        
                        // Normalized status for logic checks
                        const status = hire.status;
                        const isActionRequired = (isBuyer && (status === 'starting' || status === 'pending_start' || status === 'reviewing' || status === 'pending_end')) ||
                                                 (!isBuyer && (status === 'accepted' || status === 'running' || status === 'active'));

                        return (
                            <div key={hire.id} className={`bg-white rounded-[2rem] p-6 lg:p-8 shadow-sm border transition-all hover:shadow-md ${isActionRequired ? 'border-l-8 border-l-emerald-500 border-t border-r border-b border-slate-100' : 'border border-slate-100'}`}>
                                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
                                    
                                    {/* Profile Image */}
                                    <div className="flex-shrink-0 mx-auto lg:mx-0">
                                        <img
                                            src={loadImage(otherParty?.picture)}
                                            alt={otherParty?.name}
                                            className="w-20 h-20 rounded-2xl object-cover border-4 border-slate-50 shadow-sm bg-slate-100"
                                        />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-grow w-full text-center lg:text-left">
                                        <div className="flex flex-col md:flex-row md:items-center justify-between mb-2 gap-3">
                                            <h3 className="text-xl font-black text-slate-800">{otherParty?.name || 'Unknown User'}</h3>
                                            <StatusBadge status={status} />
                                        </div>
                                        
                                        <p className="text-slate-500 font-medium mb-4 line-clamp-2">{hire.description || 'No description provided.'}</p>
                                        
                                        <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-xs font-bold text-slate-400 uppercase tracking-wider">
                                            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                                                <FaMoneyBillWave className="text-emerald-500" /> 
                                                <span className="text-slate-700">৳{hire.total_amount || hire.agreed_amount}</span>
                                            </div>
                                            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                                                <FaCalendarAlt className="text-blue-500" />
                                                <span className="text-slate-700">
                                                    {new Date(hire.start_date || hire.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                            {(status === 'running' || status === 'active') && (
                                                <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 text-blue-700">
                                                    <FaClock /> <JobTimer startTime={hire.actual_start_time || hire.createdAt} />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="w-full lg:w-auto flex flex-col gap-2 min-w-[200px]">
                                        {/* BUYER ACTIONS */}
                                        {isBuyer && (
                                            <>
                                                {(status === 'starting' || status === 'pending_start') && (
                                                    <button
                                                        onClick={() => updateStatus(hire.id, 'running')}
                                                        className="w-full py-3 bg-purple-600 text-white rounded-xl font-bold shadow-lg shadow-purple-200 hover:bg-purple-700 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <FaPlay size={12} /> Confirm Start
                                                    </button>
                                                )}
                                                {(status === 'reviewing' || status === 'pending_end') && (
                                                    <button
                                                        onClick={() => updateStatus(hire.id, 'completed')}
                                                        className="w-full py-3 bg-emerald-600 text-white rounded-xl font-bold shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <FaCheckCircle size={14} /> Confirm & Pay
                                                    </button>
                                                )}
                                                {['running', 'active'].includes(status) && (
                                                    <button
                                                        onClick={() => navigate(`/track-worker/${hire.id}`)}
                                                        className="w-full py-3 bg-blue-100 text-blue-700 rounded-xl font-bold hover:bg-blue-200 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <FaMapMarkerAlt size={12} /> Track Live
                                                    </button>
                                                )}
                                            </>
                                        )}

                                        {/* WORKER ACTIONS */}
                                        {!isBuyer && (
                                            <>
                                                {status === 'accepted' && (
                                                    <button
                                                        onClick={() => updateStatus(hire.id, 'starting')}
                                                        className="w-full py-3 bg-purple-600 text-white rounded-xl font-bold shadow-lg shadow-purple-200 hover:bg-purple-700 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <FaPlay size={12} /> Start Job
                                                    </button>
                                                )}
                                                {(status === 'running' || status === 'active') && (
                                                    <button
                                                        onClick={() => updateStatus(hire.id, 'reviewing')}
                                                        className="w-full py-3 bg-orange-500 text-white rounded-xl font-bold shadow-lg shadow-orange-200 hover:bg-orange-600 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <FaFlagCheckered size={14} /> End Job
                                                    </button>
                                                )}
                                                {['accepted', 'starting', 'running', 'active'].includes(status) && (
                                                    <button
                                                        onClick={() => navigate(`/job-tracking/${hire.id}`)}
                                                        className="w-full py-3 bg-emerald-100 text-emerald-700 rounded-xl font-bold hover:bg-emerald-200 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <FaMapMarkerAlt size={12} /> Navigate to Site
                                                    </button>
                                                )}
                                            </>
                                        )}

                                        {/* COMMON ACTIONS */}
                                        {(status === 'pending' || status === 'accepted' || status === 'offered') && (
                                            <button
                                                onClick={() => updateStatus(hire.id, 'cancelled')}
                                                className="w-full py-3 bg-red-50 text-red-600 rounded-xl font-bold hover:bg-red-100 transition-colors flex items-center justify-center gap-2 border border-red-100"
                                            >
                                                <FaTimesCircle /> Cancel
                                            </button>
                                        )}

                                        {/* STATUS MESSAGES */}
                                        {(status === 'starting' || status === 'pending_start') && !isBuyer && (
                                            <p className="text-xs text-purple-600 font-bold text-center bg-purple-50 py-2 rounded-lg">Waiting for Buyer confirmation...</p>
                                        )}
                                        {(status === 'reviewing' || status === 'pending_end') && isBuyer && (
                                            <p className="text-xs text-orange-600 font-bold text-center bg-orange-50 py-2 rounded-lg">Worker requested completion.</p>
                                        )}
                                        {(status === 'reviewing' || status === 'pending_end') && !isBuyer && (
                                            <p className="text-xs text-orange-600 font-bold text-center bg-orange-50 py-2 rounded-lg">Waiting for Payment...</p>
                                        )}
                                        {status === 'completed' && (
                                            <div className="flex flex-col gap-2">
                                                <div className="w-full py-3 bg-slate-100 text-slate-500 rounded-xl font-bold flex items-center justify-center gap-2">
                                                    <FaCheckCircle /> Completed
                                                </div>
                                                {!hire.rating ? (
                                                    <button 
                                                        onClick={() => {
                                                            setRatingData({ 
                                                                workerId: hire.worker?.id, 
                                                                workerName: hire.worker?.name, 
                                                                hireId: hire.id,
                                                                type: hire.type || 'Worker_Hire'
                                                            });
                                                            setIsRatingModalOpen(true);
                                                        }}
                                                        className="px-6 py-3 bg-yellow-500 text-white rounded-xl font-bold shadow-lg shadow-yellow-200 hover:bg-yellow-600 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <FaStar /> Rate Professional
                                                    </button>
                                                ) : (
                                                    <div className="text-center pt-1">
                                                        <div className="flex justify-center text-yellow-400 gap-0.5">
                                                            {[...Array(hire.rating.rating || 0)].map((_, i) => <FaStar key={i} size={10} />)}
                                                        </div>
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">Feedback Given</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <RatingModal 
                isOpen={isRatingModalOpen}
                onClose={() => setIsRatingModalOpen(false)}
                workerName={ratingData.workerName}
                workerId={ratingData.workerId}
                hireId={ratingData.hireId}
                type={ratingData.type}
                onSuccess={fetchHires}
            />
        </div>
    );
};

export default MyHiresPage;
